import { Add, HelpOutline } from '@mui/icons-material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Collapse, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Box } from '@mui/system'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import ConfirmationDialog from '../../components/Dialog/ConfirmationDialog'
import DiscardChangesDialog from '../../components/Dialog/DiscardChangesDialog'
import LikeDislikeCommentSection from '../../components/LikeDislike/LikeDislikeCommentSection'
import CommentsSidebar from '../../components/Sidebar/comments/CommentsSidebar'
import TechStackTable from '../../components/Table/TechStackTable'
import navigation from '../../config/navigation'
import { useAppDispatch, useEditMode, useNotification } from '../../store/redux/hooks'
import { breadcrumbsItemsActions } from '../../store/redux/reducers/breadcrumbsSlice'
import {
  useCreateCandidateMutation,
  useDeleteCandidateMutation,
  useEditCandidateMutation,
  useGetCandidateByIdQuery,
} from '../../store/redux/reducers/candidateSlice'
import { useGetDictionariesQuery } from '../../store/redux/reducers/dictionarySlice'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { UsersTechnology } from '../../store/types'
import TargetType from '../../store/types/enums/TargetType'
import { CandidateFormValues, defaultCandidateFormValues } from '../../store/types/form/CandidateFormValues'
import Candidate from '../../store/types/models/Candidate'
import Recruitment from '../../store/types/models/Recruitment'
import { createErrorMessage } from '../../store/utils'
import sortByField from '../../store/utils/sortByField'
import { StyledButtonPrimary } from '../../styles/ButtonStyles'
import AddRecruitment from '../AddRecruitmentView/AddRecruitment'
import CandidateDetailsData from './CandidateDetailsData'
import CandidateDetailsFirstRecruitmentInputs from './CandidateDetailsFirstRecruitmentInputs'
import CandidateDetailsHeader from './CandidateDetailsHeader'
import CandidateDetailsInputs from './CandidateDetailsInputs'
import CandidateRecruitmentsTable from './CandidateRecruitmentsTable'

type CandidateDetailsViewProps = {
  newCandidate: boolean
}

const CandidateDetailsView = ({ newCandidate }: CandidateDetailsViewProps) => {
  const { displayNotification } = useNotification()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { id: candidateId } = useParams()
  const { data: candidateDetails } = useGetCandidateByIdQuery(candidateId ?? '', { skip: !candidateId })
  const [editMode, setEditMode] = useState<boolean>(newCandidate)
  const [editCandidate] = useEditCandidateMutation()
  const [createCandidate] = useCreateCandidateMutation()
  const setIsEditMode = useEditMode().setIsEditMode
  const { data: dictionariesResponse } = useGetDictionariesQuery({
    page: 0,
    size: 20,
    group: 'localization',
    sortField: 'value',
  })

  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    dispatch(setPageName(newCandidate ? t('page.name.candidates.new') : t('page.name.candidates.details')))
    setIsEditMode(true)
    dispatch(setPageIndex(PageIndex.CANDIDATES))
    if (!newCandidate && candidateDetails !== undefined) {
      dispatch(breadcrumbsItemsActions.setInitialState())
      dispatch(
        breadcrumbsItemsActions.setSelectedItems({
          candidates: `${candidateDetails.firstName} ${candidateDetails.lastName}`,
        })
      )
    } else if (newCandidate) {
      dispatch(breadcrumbsItemsActions.setInitialState())
      dispatch(
        breadcrumbsItemsActions.setSelectedItems({
          candidates: t('page.name.candidates.new'),
        })
      )
    }
  }, [candidateId, dispatch, t, newCandidate, candidateDetails, setIsEditMode])

  const { handleSubmit, control, formState, reset, watch } = useForm<CandidateFormValues>({
    defaultValues: defaultCandidateFormValues,
  })

  const { isDirty } = formState

  const updateForm = useCallback(
    (candidateData: Candidate | null) => {
      if (candidateData === null) {
        return
      }

      const updatedData: CandidateFormValues = {
        ...candidateData,
        localization: candidateData.localization.id,
        dateOfApplication: dayjs(candidateData.dateOfApplication).utc(true),
        technologies: sortByField(candidateData.technologies, 'name'),
      }

      reset(updatedData)
    },
    [reset]
  )

  useEffect(() => {
    if (candidateDetails) {
      updateForm(candidateDetails)
    } else {
      updateForm(null)
    }
  }, [updateForm, candidateDetails, candidateId])

  const handleEdit = async (data: CandidateFormValues) => {
    if (!candidateId) {
      return
    }
    try {
      await editCandidate({ id: candidateId, candidate: data }).unwrap()
      displayNotification({ message: t('page.candidateDetails.alert.candidateEdited'), type: 'success' })
      reset(data)
      setEditMode(false)
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({
        message: `${t('page.users.message.error')} ${errorDescription}`,
        type: 'error',
      })
    }
  }

  const handleCreate = async (data: CandidateFormValues) => {
    const createData = {
      ...data,
      email: data.email === '' ? undefined : data.email,
      phoneNumber: data.phoneNumber === '' ? undefined : data.phoneNumber,
      responsibles: data.responsibles?.map(responsible => responsible.value),
      localization: data.localization === '' ? undefined : data.localization,
    }
    try {
      await createCandidate(createData).unwrap()
      displayNotification({ message: t('page.candidateDetails.alert.candidateCreated'), type: 'success' })
      reset(data)
      setTimeout(() => navigate(navigation.candidates), 0)
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({ message: `${t('page.users.message.error')} ${errorDescription}`, type: 'error' })
    }
  }

  const handleSubmitEdit = () => {
    handleSubmit(data => handleEdit(data))()
  }

  const handleSubmitCreate = () => {
    handleSubmit(data => handleCreate(data))()
  }

  const { replace } = useFieldArray({
    name: 'technologies',
    control,
  })

  const replaceArray = (technologies: UsersTechnology[]) => {
    return replace(sortByField(technologies, 'name'))
  }

  const [open, setOpen] = useState(false)

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const checkUnsavedChanges = () => {
    return isDirty
  }

  const handleCancelButtonClick = () => {
    if (!isDirty) {
      setEditMode(false)
      setIsEditMode(false)
    } else {
      setIsCancelDialogOpen(true)
    }
  }

  const resetForm = () => {
    setEditMode(false)
    reset()
  }

  const [commentSidebarOpen, setCommentSidebarOpen] = useState(false)

  const handleCommentClick = () => {
    setCommentSidebarOpen(true)
  }

  const [deleteCandidate] = useDeleteCandidateMutation()
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false)

  const handleDeleteButtonClick = () => {
    setIsConfirmDeleteDialogOpen(true)
  }
  const handleDeleteCandidate = async () => {
    if (!candidateId) {
      return
    }

    try {
      await deleteCandidate(candidateId).unwrap()
      displayNotification({ message: t('page.candidates.alert.candidateDeleted'), type: 'success' })
      navigate(navigation.candidates)
    } catch (_) {
      displayNotification({ message: t('page.candidates.alert.candidateDeleteError'), type: 'error' })
    }
  }

  const [commentsCount, setCommentsCount] = useState<number>(0)
  const [editedRecruitmentId, setEditedRecruitmentId] = useState<string | undefined>(undefined)

  const [isRecruitmentForm, setIsRecruitmentForm] = useState<boolean>(false)
  const handleAddRecruitmentClick = () => {
    setIsRecruitmentForm(true)
  }

  const handleRecruitmentRowClick = (recruitmentId: string) => {
    setIsRecruitmentForm(true)
    setEditedRecruitmentId(recruitmentId)
  }

  useEffect(() => {
    if (!isRecruitmentForm) setEditedRecruitmentId(undefined)
  }, [isRecruitmentForm])

  const latestRecruitment = useMemo(() => {
    return candidateDetails?.recruitments?.reduce((latest: Recruitment | undefined, current) => {
      if (!latest) return current
      return dayjs(current.completionDate).isAfter(latest.completionDate) ? current : latest
    }, undefined)
  }, [candidateDetails])

  return isRecruitmentForm ? (
    <AddRecruitment
      setIsRecruitmentForm={setIsRecruitmentForm}
      editedRecruitmentId={editedRecruitmentId}
      latestRecruitment={latestRecruitment}
    />
  ) : (
    <Stack
      sx={{
        px: { xs: 0, md: 2 },
        gap: 4,
      }}
    >
      <CandidateDetailsHeader
        candidateDetails={candidateDetails}
        editMode={editMode}
        setEditMode={setEditMode}
        setOpen={setOpen}
        handleSubmitEdit={handleSubmitEdit}
        handleSubmitCreate={handleSubmitCreate}
        newCandidate={newCandidate}
        handleCancelButtonClick={handleCancelButtonClick}
        handleDeleteButtonClick={handleDeleteButtonClick}
      />
      {!newCandidate && (
        <Stack direction="row" gap={1} sx={isDownSm ? { alignItems: 'center', justifyContent: 'center' } : {}}>
          <LikeDislikeCommentSection
            big
            handleCommentClick={handleCommentClick}
            targetId={candidateId || ''}
            targetType={TargetType.CANDIDATE}
            upVoters={candidateDetails?.upVoters}
            downVoters={candidateDetails?.downVoters}
            commentsCount={commentsCount}
            isFollowingCandidate={candidateDetails?.isFollowing}
          />
        </Stack>
      )}
      <Stack sx={{ gap: 2 }}>
        <Stack direction={isDownSm && newCandidate ? 'column' : 'row'} sx={{ gap: 2, alignContent: 'center' }}>
          <Typography component={'h3'} variant="h4" color="text.highEmphasis" alignSelf={'center'}>
            {t('page.candidateDetails.collapseHeadline')}
          </Typography>
          <Tooltip
            title={!open ? t('page.candidateDetails.expandTooltip') : t('page.candidateDetails.collapseTooltip')}
            placement={'right'}
          >
            <IconButton
              sx={{
                color: 'inherit',
                '&:hover': { color: 'inherit' },
                backgroundColor: 'background.elevation2',
                width: 'fit-content',
                display: newCandidate ? 'none' : 'auto',
              }}
              onClick={event => {
                event.stopPropagation()
                setOpen(!open)
              }}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
        <Collapse in={newCandidate ? true : open} unmountOnExit>
          <Stack direction={isDownSm ? 'column' : 'row'} sx={{ gap: 4, px: newCandidate ? 0 : 2 }}>
            <Stack sx={{ gap: 2 }} width={{ xs: '100%', smMid: '50%', md: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 2 }}>
                <Typography component={'h4'} variant="h5" color="text.highEmphasis">
                  {t('page.candidateDetails.basicInformation')}
                </Typography>
                {editMode && (
                  <Typography variant="caption">{t('page.usersProfile.form.headers.requiredFields')}</Typography>
                )}
              </Box>
              {!editMode ? (
                <CandidateDetailsData candidateDetails={candidateDetails} />
              ) : (
                <CandidateDetailsInputs control={control} localizations={dictionariesResponse?.content} />
              )}
            </Stack>
            <Stack sx={{ gap: 2 }} width={{ xs: '100%', smMid: '50%', md: '100%' }}>
              <Stack direction="row" gap={1} alignItems="center">
                <Typography variant="h5" color="text.highEmphasis" id={'tech-header'}>
                  {t('page.usersProfile.form.headers.technologies')}
                </Typography>
                <Tooltip title={<Typography variant="caption">{t('page.usersProfile.tooltip.techStack')}</Typography>}>
                  <HelpOutline sx={{ color: 'text.mediumEmphasis', fontSize: 18 }} />
                </Tooltip>
              </Stack>
              <TechStackTable
                editable={editMode}
                replace={replaceArray}
                watchedTechnologies={watch('technologies') === null ? [] : watch('technologies')}
                userTechnologies={candidateDetails?.technologies === null ? [] : candidateDetails?.technologies}
                smallDefaultTable
              />
              {newCandidate && <CandidateDetailsFirstRecruitmentInputs control={control} />}
            </Stack>
          </Stack>
        </Collapse>
      </Stack>
      {!editMode && (
        <>
          <Stack
            direction={isDownSm ? 'column' : 'row'}
            sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
          >
            <Typography variant="h4" color="text.highEmphasis">
              {t('page.candidateDetails.recruitments')}
            </Typography>
            <StyledButtonPrimary
              variant="contained"
              startIcon={<Add />}
              size="medium"
              onClick={handleAddRecruitmentClick}
            >
              {t('components.button.addRecruitment')}
            </StyledButtonPrimary>
          </Stack>
          <CandidateRecruitmentsTable candidateId={candidateId} handleRecruitmentRowClick={handleRecruitmentRowClick} />
        </>
      )}
      <DiscardChangesDialog
        open={isCancelDialogOpen}
        checkUnsavedChanges={checkUnsavedChanges}
        setIsCancelDialogOpen={setIsCancelDialogOpen}
        resetForm={resetForm}
      />
      <CommentsSidebar
        open={commentSidebarOpen}
        setOpen={setCommentSidebarOpen}
        commentedCandidate={
          candidateDetails
            ? {
                id: candidateDetails.id,
                firstName: candidateDetails.firstName,
                lastName: candidateDetails.lastName,
                localization: candidateDetails.localization.value,
              }
            : undefined
        }
        setCommentsCount={setCommentsCount}
      />

      <ConfirmationDialog
        open={isConfirmDeleteDialogOpen}
        setOpen={setIsConfirmDeleteDialogOpen}
        handleConfirm={handleDeleteCandidate}
        content={
          <Trans
            i18nKey="page.candidates.dialog.delete.content"
            values={{ name: `${candidateDetails?.firstName} ${candidateDetails?.lastName}` }}
          />
        }
        title={t('page.candidates.dialog.delete.title')}
      />
    </Stack>
  )
}

export default CandidateDetailsView
